import React, { useState, useEffect, useCallback } from 'react';
import { Handshake, RefreshCw, Search, Check, X, CornerUpLeft, Ban, KeyRound, ChevronLeft } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { rootFetch, stepUpChallenge, formatTimestamp } from '../../lib/rootSecurity';

// SuperAdmin Partners & Channels (Slice A). READ/review is any full SuperAdmin; APPROVE and partner
// STATUS changes additionally require the Root tier + a fresh step-up — enforced by the backend and
// surfaced here with the SAME page-level prompt User Management uses (no new MFA engine). Nothing here
// touches commercial products, prices, drafts, packs, invoices or Tenant360.

const APP_BASE = '/api/v1/super-admin/partners';

const APP_STATUS_META = {
  received: { label: 'Received', cls: 'bg-gray-100 text-gray-700' },
  contact_verified: { label: 'Contact verified', cls: 'bg-blue-100 text-blue-800' },
  pending_review: { label: 'Pending review', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800' },
  returned: { label: 'Returned', cls: 'bg-purple-100 text-purple-800' },
};
const PARTNER_STATUS_META = {
  active: { label: 'Active', cls: 'bg-green-100 text-green-800' },
  suspended: { label: 'Suspended', cls: 'bg-amber-100 text-amber-800' },
  terminated: { label: 'Terminated', cls: 'bg-red-100 text-red-800' },
};
const meta = (map, s) => map[String(s || '').toLowerCase()] || { label: s || '—', cls: 'bg-gray-100 text-gray-700' };

const PartnersChannels = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState('applications');
  const [overview, setOverview] = useState(null);
  const [applications, setApplications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [permission, setPermission] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [reason, setReason] = useState('');

  // Page-level step-up (identical pattern to User Management): on 403 MFA_STEP_UP_REQUIRED we park the
  // action and replay it once after the code verifies.
  const [pendingStepUp, setPendingStepUp] = useState(null); // { label, retry }
  const [stepUpCode, setStepUpCode] = useState('');
  const [stepUpBusy, setStepUpBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(''); setPermission(true);
    try {
      const [ov, apps, regs] = await Promise.all([
        rootFetch(`${APP_BASE}/overview`),
        rootFetch(`${APP_BASE}/applications`),
        rootFetch(`${APP_BASE}/registry`),
      ]);
      setOverview(ov || null);
      setApplications((apps && apps.applications) || []);
      setPartners((regs && regs.partners) || []);
    } catch (err) {
      if (err && (err.status === 401 || err.status === 403)) {
        setPermission(false);
      } else {
        setError((err && (typeof err.detail === 'string' ? err.detail : err.message)) || 'Failed to load.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshSelection = async () => {
    await load();
    if (selectedApp) {
      try { setSelectedApp(await rootFetch(`${APP_BASE}/applications/${selectedApp.application_id}`)); }
      catch (_e) { setSelectedApp(null); }
    }
    if (selectedPartner) {
      try { setSelectedPartner(await rootFetch(`${APP_BASE}/registry/${selectedPartner.partner_id}`)); }
      catch (_e) { setSelectedPartner(null); }
    }
  };

  // --- Governed actions (Root + step-up) ---
  const reviewApplication = (app, decision) => {
    const run = async () => {
      await rootFetch(`${APP_BASE}/applications/${app.application_id}/review`, {
        method: 'POST', body: JSON.stringify({ decision, reason: reason.trim() || null }),
      });
    };
    const label = `${decision === 'approve' ? 'Approve' : decision === 'reject' ? 'Reject' : 'Return'} ${app.legal_name || app.application_id}`;
    runGoverned(run, label);
  };

  const changePartnerStatus = (partner, status) => {
    const run = async () => {
      await rootFetch(`${APP_BASE}/registry/${partner.partner_id}/status`, {
        method: 'POST', body: JSON.stringify({ status, reason: reason.trim() || null }),
      });
    };
    runGoverned(run, `Set ${partner.legal_name || partner.partner_id} → ${status}`);
  };

  const runGoverned = async (run, label) => {
    try {
      await run();
      toast({ title: 'Done', description: `${label} — completed.` });
      setReason('');
      await refreshSelection();
    } catch (err) {
      if (stepUpChallenge(err)) {
        setPendingStepUp({ label, retry: run });
        return;
      }
      if (err && err.status === 409) {
        toast({ title: 'Not allowed', description: (err.body && err.body.detail && err.body.detail.code) || 'Invalid state transition.', variant: 'destructive' });
        return;
      }
      toast({ title: 'Action failed', description: typeof err.detail === 'string' ? err.detail : err.message, variant: 'destructive' });
    }
  };

  const submitStepUp = async () => {
    if (!/^\d{6}$/.test(stepUpCode.trim())) {
      toast({ title: 'Enter a code', description: 'Six digits from your authenticator app.', variant: 'destructive' });
      return;
    }
    setStepUpBusy(true);
    try {
      await rootFetch('/api/v1/super-admin/security/step-up', { method: 'POST', body: JSON.stringify({ code: stepUpCode.trim() }) });
    } catch (err) {
      toast({ title: 'Code not accepted', description: typeof err.detail === 'string' ? err.detail : err.message, variant: 'destructive' });
      setStepUpBusy(false);
      return;
    }
    const parked = pendingStepUp;
    setPendingStepUp(null); setStepUpCode(''); setStepUpBusy(false);
    if (!parked) return;
    try {
      await parked.retry();
      toast({ title: 'Done', description: `${parked.label} — completed.` });
      setReason('');
      await refreshSelection();
    } catch (err) {
      toast({ title: 'Action failed', description: typeof err.detail === 'string' ? err.detail : err.message, variant: 'destructive' });
    }
  };

  const q = search.toLowerCase();
  const apps = applications.filter((a) => !q || (a.legal_name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q));
  const regs = partners.filter((p) => !q || (p.legal_name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q));

  if (!permission) {
    return (
      <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 text-amber-900">
        You don't have access to Partners &amp; Channels, or your session has expired. Please sign in again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Handshake className="h-7 w-7 text-purple-700" /> Partners &amp; Channels</h1>
          <p className="text-gray-600 mt-1">Partner applications, registry and lifecycle governance.</p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Overview tiles */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tile n={sum(overview.applications)} label="Applications" />
          <Tile n={(overview.applications && overview.applications.pending_review) || 0} label="Pending review" accent="text-amber-600" />
          <Tile n={(overview.partners && overview.partners.active) || 0} label="Active partners" accent="text-green-600" />
          <Tile n={(overview.partners && overview.partners.suspended) || 0} label="Suspended" accent="text-red-600" />
        </div>
      )}

      {pendingStepUp && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="root-step-up">
          <p className="text-sm text-blue-900 flex items-center gap-2 mb-2">
            <KeyRound className="h-4 w-4" /> {pendingStepUp.label} needs a fresh authenticator code before it can proceed.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input className="border rounded-md px-3 py-2 text-sm w-40" inputMode="numeric" maxLength={6}
              placeholder="000000" aria-label="Authenticator code"
              value={stepUpCode} onChange={(e) => setStepUpCode(e.target.value.replace(/\D/g, ''))} />
            <Button size="sm" onClick={submitStepUp} disabled={stepUpBusy}>{stepUpBusy ? 'Verifying…' : 'Verify and continue'}</Button>
            <Button size="sm" variant="outline" disabled={stepUpBusy} onClick={() => { setPendingStepUp(null); setStepUpCode(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['applications', 'registry'].map((t) => (
          <button key={t} onClick={() => { setTab(t); setSelectedApp(null); setSelectedPartner(null); }}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t === 'applications' ? 'Applications' : 'Partner registry'}
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input className="pl-9" placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Genuine backend error — shown separately from an empty database, never as zero rows. */}
      {error && !loading && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800" role="alert" data-testid="partners-error">
          Couldn't load partner data: {error}. This is a service error, not an empty list — please Refresh or try again.
        </div>
      )}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {/* APPLICATIONS */}
      {tab === 'applications' && !loading && !error && (
        selectedApp ? (
          <DetailPanel onBack={() => setSelectedApp(null)} title={selectedApp.legal_name}>
            <KV k="Status"><Badge className={meta(APP_STATUS_META, selectedApp.status).cls}>{meta(APP_STATUS_META, selectedApp.status).label}</Badge></KV>
            <KV k="Contact">{selectedApp.contact_name} · {selectedApp.email}</KV>
            <KV k="Type">{selectedApp.partner_type}</KV>
            <KV k="Contact verified">{selectedApp.contact_verified ? 'Yes' : 'No'}</KV>
            <KV k="Verification email">{selectedApp.verify_delivery_status || '—'}</KV>
            <KV k="Country">{selectedApp.country || '—'}</KV>
            <KV k="Website">{selectedApp.company_website || '—'}</KV>
            <KV k="Received">{formatTimestamp(selectedApp.created_at)}</KV>
            {selectedApp.note && <KV k="Note">{selectedApp.note}</KV>}

            {['received', 'contact_verified', 'pending_review', 'returned'].includes(selectedApp.status) && (
              <div className="mt-4 border-t pt-4 space-y-3">
                <Input placeholder="Reason / note (recorded in audit)" value={reason} onChange={(e) => setReason(e.target.value)} />
                {!selectedApp.contact_verified && (
                  <p className="text-xs text-amber-700">Approval is disabled until the applicant verifies their contact email.</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" disabled={!selectedApp.contact_verified}
                    className="bg-green-600 hover:bg-green-700 text-white" onClick={() => reviewApplication(selectedApp, 'approve')}>
                    <Check className="h-4 w-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reviewApplication(selectedApp, 'return')}>
                    <CornerUpLeft className="h-4 w-4 mr-1" /> Return for correction
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => reviewApplication(selectedApp, 'reject')}>
                    <X className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </DetailPanel>
        ) : (
          <Table empty="No partner applications yet." rows={apps} cols={['Company', 'Contact', 'Type', 'Status', '']}
            render={(a) => (
              <tr key={a.application_id} className="border-t hover:bg-gray-50">
                <td className="py-2 px-3 font-medium text-gray-900">{a.legal_name}</td>
                <td className="py-2 px-3 text-gray-600">{a.email}</td>
                <td className="py-2 px-3">{a.partner_type}</td>
                <td className="py-2 px-3"><Badge className={meta(APP_STATUS_META, a.status).cls}>{meta(APP_STATUS_META, a.status).label}</Badge></td>
                <td className="py-2 px-3 text-right"><Button size="sm" variant="outline" onClick={() => setSelectedApp(a)}>Review</Button></td>
              </tr>
            )} />
        )
      )}

      {/* REGISTRY */}
      {tab === 'registry' && !loading && !error && (
        selectedPartner ? (
          <DetailPanel onBack={() => setSelectedPartner(null)} title={selectedPartner.legal_name}>
            <KV k="Partner ID">{selectedPartner.partner_id}</KV>
            <KV k="Status"><Badge className={meta(PARTNER_STATUS_META, selectedPartner.status).cls}>{meta(PARTNER_STATUS_META, selectedPartner.status).label}</Badge></KV>
            <KV k="Contact">{selectedPartner.contact_name} · {selectedPartner.email}</KV>
            <KV k="Type">{selectedPartner.partner_type}</KV>
            <KV k="Approved by">{selectedPartner.approved_by || '—'} ({formatTimestamp(selectedPartner.approved_at)})</KV>
            {selectedPartner.status !== 'terminated' && (
              <div className="mt-4 border-t pt-4 space-y-3">
                <Input placeholder="Reason / note (recorded in audit)" value={reason} onChange={(e) => setReason(e.target.value)} />
                <div className="flex flex-wrap gap-2">
                  {selectedPartner.status === 'active' && (
                    <Button size="sm" variant="outline" className="text-amber-700 border-amber-200" onClick={() => changePartnerStatus(selectedPartner, 'suspended')}>
                      <Ban className="h-4 w-4 mr-1" /> Suspend
                    </Button>
                  )}
                  {selectedPartner.status === 'suspended' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => changePartnerStatus(selectedPartner, 'active')}>
                      <Check className="h-4 w-4 mr-1" /> Reinstate
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => changePartnerStatus(selectedPartner, 'terminated')}>
                    <X className="h-4 w-4 mr-1" /> Terminate
                  </Button>
                </div>
              </div>
            )}
          </DetailPanel>
        ) : (
          <Table empty="No registered partners yet." rows={regs} cols={['Company', 'Contact', 'Type', 'Status', '']}
            render={(p) => (
              <tr key={p.partner_id} className="border-t hover:bg-gray-50">
                <td className="py-2 px-3 font-medium text-gray-900">{p.legal_name}</td>
                <td className="py-2 px-3 text-gray-600">{p.email}</td>
                <td className="py-2 px-3">{p.partner_type}</td>
                <td className="py-2 px-3"><Badge className={meta(PARTNER_STATUS_META, p.status).cls}>{meta(PARTNER_STATUS_META, p.status).label}</Badge></td>
                <td className="py-2 px-3 text-right"><Button size="sm" variant="outline" onClick={() => setSelectedPartner(p)}>Open</Button></td>
              </tr>
            )} />
        )
      )}
    </div>
  );
};

const sum = (obj) => Object.values(obj || {}).reduce((a, b) => a + (Number(b) || 0), 0);
const Tile = ({ n, label, accent = 'text-gray-900' }) => (
  <div className="bg-white rounded-xl shadow-sm p-4 border">
    <div className={`text-2xl font-bold ${accent}`}>{n}</div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);
const KV = ({ k, children }) => (
  <div className="flex gap-3 py-1 text-sm"><span className="w-40 shrink-0 text-gray-500">{k}</span><span className="text-gray-900">{children}</span></div>
);
const DetailPanel = ({ onBack, title, children }) => (
  <div className="bg-white rounded-xl border shadow-sm p-5">
    <button onClick={onBack} className="text-sm text-purple-700 flex items-center gap-1 mb-3"><ChevronLeft className="h-4 w-4" /> Back</button>
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </div>
);
const Table = ({ rows, cols, render, empty }) => (
  <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
    {rows.length === 0 ? (
      <p className="p-6 text-sm text-gray-500">{empty}</p>
    ) : (
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500">{cols.map((c, i) => <th key={i} className="py-2 px-3 font-medium">{c}</th>)}</tr></thead>
        <tbody>{rows.map(render)}</tbody>
      </table>
    )}
  </div>
);

export default PartnersChannels;
